import { useCallback, useEffect, useRef, useState } from 'react'

const SDK = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1'

export type CastPhase = 'unsupported' | 'loading' | 'no-devices' | 'ready' | 'connecting' | 'connected'

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 4000
/** Playback must hold this long before failed attempts are forgiven. */
const STABLE_PLAYBACK_MS = 30_000
/** After issuing a reload, ignore IDLE for this long: the swap reports it. */
const RELOAD_SETTLE_MS = 8_000

/** The SDK still holds a session whose underlying connection has died. */
function isDeadConnection(err: unknown): boolean {
  const message = typeof err === 'string' ? err : err instanceof Error ? err.message : ''
  return /presentation connection is disconnected|PresentationConnection/i.test(message)
}

/** Why the receiver went idle, in plain language. */
const IDLE_REASON: Record<string, string> = {
  ERROR: 'el receptor ha donat error reproduint',
  FINISHED: 'el receptor ha donat el flux per acabat',
  CANCELLED: "algú ha aturat l'emissió",
  INTERRUPTED: 'una altra emissió ha pres el dispositiu',
}

/**
 * Casting driven by the browser, so the device list is the one on *this*
 * device's network. Discovery belongs to the browser: no web API can do mDNS.
 */
export function useCastSender() {
  const [phase, setPhase] = useState<CastPhase>('loading')
  const [deviceName, setDeviceName] = useState<string | null>(null)
  const [volume, setVolumeState] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [playerState, setPlayerState] = useState<string | null>(null)
  /** Survives the session ending, which is exactly when it matters. */
  const [lastStop, setLastStop] = useState<string | null>(null)

  const contextRef = useRef<CastContext | null>(null)
  const syncRef = useRef<() => void>(() => {})
  /** What we last asked the receiver to play, so we can ask again. */
  const mediaRef = useRef<{ url: string; title: string } | null>(null)
  const relaunchRef = useRef<(() => void) | null>(null)
  const attemptsRef = useRef(0)
  /** Set while the user is deliberately stopping, so we do not fight them. */
  const stoppingRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let controller: RemotePlayerController | null = null
    let onPlayerState: (() => void) | null = null
    // The SDK exposes this as a lookup table; fall back to the literal event
    // name so a missing key cannot silently disable the diagnostics.
    let evState = 'playerStateChanged'
    let relaunchPending = false
    let stableTimer: number | null = null

    const start = () => {
      const framework = window.cast?.framework
      const api = window.chrome?.cast
      if (!framework || !api) {
        if (!cancelled) setPhase('unsupported')
        return
      }

      const context = framework.CastContext.getInstance()
      context.setOptions({
        receiverApplicationId: api.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: api.AutoJoinPolicy['ORIGIN_SCOPED'],
      })
      contextRef.current = context

      const sync = () => {
        if (cancelled) return
        const session = context.getCurrentSession()
        setDeviceName(session?.getCastDevice()?.friendlyName ?? null)
        setVolumeState(session ? session.getVolume() : null)
        switch (context.getCastState()) {
          case 'NO_DEVICES_AVAILABLE':
            setPhase('no-devices')
            break
          case 'CONNECTING':
            setPhase('connecting')
            break
          case 'CONNECTED':
            setPhase('connected')
            break
          default:
            setPhase('ready')
        }
      }
      syncRef.current = sync

      context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, sync)
      context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, sync)

      // The receiver keeps reporting after load() resolves, and that is where
      // a stream that dies mid-playback actually shows up.
      const player = new framework.RemotePlayer()
      controller = new framework.RemotePlayerController(player)
      onPlayerState = () => {
        if (cancelled) return
        const state = player.playerState ?? null
        setPlayerState(state)

        if (state === 'PLAYING') {
          // Only a stretch of real playback earns the retries back. Resetting on
          // the first PLAYING let every brief start refill them, so a stream
          // that kept failing a second in retried for ever.
          if (stableTimer === null) {
            stableTimer = window.setTimeout(() => {
              stableTimer = null
              attemptsRef.current = 0
            }, STABLE_PLAYBACK_MS)
          }
          setLastStop(null)
          return
        }
        if (stableTimer !== null) {
          window.clearTimeout(stableTimer)
          stableTimer = null
        }
        if (state !== 'IDLE') return

        // One retry in flight at a time. A reload of our own replaces the
        // media, which itself reports IDLE; reacting to that is what turned
        // one failure into a loop of reloads.
        if (relaunchPending) return

        const reason = context.getCurrentSession()?.getMediaSession()?.idleReason ?? null
        const label = reason
          ? `Emissió aturada: ${IDLE_REASON[reason] ?? reason}`
          : 'Emissió aturada pel receptor, sense motiu declarat'

        // The receiver gives up on the first fatal error where hls.js would
        // retry, which is why the phone survives and the TV does not. Only
        // retry failures of the stream itself: INTERRUPTED means another load
        // took over -- usually ours -- and CANCELLED means somebody stopped it.
        const recoverable =
          !stoppingRef.current && mediaRef.current !== null && (reason === 'ERROR' || reason === 'FINISHED')
        if (recoverable && attemptsRef.current < MAX_RETRIES) {
          attemptsRef.current += 1
          relaunchPending = true
          setLastStop(`${label}. Reintentant (${attemptsRef.current}/${MAX_RETRIES})…`)
          window.setTimeout(() => {
            relaunchRef.current?.()
            // Give the new load time to start before trusting IDLE again.
            window.setTimeout(() => {
              relaunchPending = false
            }, RELOAD_SETTLE_MS)
          }, RETRY_DELAY_MS)
        } else {
          setLastStop(recoverable ? `${label}. Reintents esgotats.` : label)
        }
      }
      evState = framework.RemotePlayerEventType['PLAYER_STATE_CHANGED'] ?? evState
      // Recovery listens to player state only. The same handler was also on
      // IS_MEDIA_LOADED_CHANGED, so every failure fired it twice and scheduled
      // two reloads that then interrupted each other.
      controller.addEventListener(evState, onPlayerState)

      sync()
    }

    if (window.cast?.framework) {
      start()
    } else {
      window.__onGCastApiAvailable = (available: boolean) => {
        if (available) start()
        else if (!cancelled) setPhase('unsupported')
      }
      const script = document.createElement('script')
      script.src = SDK
      script.onerror = () => !cancelled && setPhase('unsupported')
      document.head.appendChild(script)
      window.setTimeout(() => {
        if (!cancelled && !window.cast?.framework) setPhase('unsupported')
      }, 10_000)
    }

    return () => {
      cancelled = true
      if (stableTimer !== null) window.clearTimeout(stableTimer)
      if (controller && onPlayerState) controller.removeEventListener(evState, onPlayerState)
    }
  }, [])

  /** Hands the media to whatever session is currently open. */
  const loadInto = useCallback(async (url: string, title: string): Promise<void> => {
    const api = window.chrome?.cast
    const session = contextRef.current?.getCurrentSession()
    if (!api || !session) throw new Error('sense sessió amb el dispositiu')

    const media = new api.media.MediaInfo(url, 'application/x-mpegURL')
    media['streamType'] = api.media.StreamType['LIVE']
    if (api.media.HlsVideoSegmentFormat?.['MPEG2_TS']) {
      media['hlsVideoSegmentFormat'] = api.media.HlsVideoSegmentFormat['MPEG2_TS']
    }
    const metadata = new api.media.GenericMediaMetadata()
    metadata['title'] = title
    media['metadata'] = metadata
    await session.loadMedia(new api.media.LoadRequest(media))
  }, [])

  /**
   * Drops a session whose connection is already gone. On Android the SDK sits
   * on the Presentation API, and when that connection breaks the context keeps
   * reporting the session as live: every command then fails with "Presentation
   * connection is disconnected", and casting again reuses the corpse instead of
   * asking for a new one. Ending it locally is the only way out short of a
   * page reload; the send this triggers may fail too, which is fine.
   */
  const discardSession = useCallback(() => {
    const context = contextRef.current
    try {
      context?.getCurrentSession()?.endSession(false)
    } catch {
      /* the connection is dead; that is the point */
    }
    try {
      context?.endCurrentSession(false)
    } catch {
      /* same */
    }
    setPlayerState(null)
    syncRef.current()
  }, [])

  // Re-issued from the idle handler, which cannot reach `loadInto` directly.
  // Assigned in an effect rather than during render, which must stay pure.
  useEffect(() => {
    relaunchRef.current = () => {
      const media = mediaRef.current
      if (!media || stoppingRef.current) return
      void loadInto(media.url, media.title).catch((err) => {
        if (isDeadConnection(err)) {
          // A new session needs the device picker, which needs a tap.
          discardSession()
          setLastStop("S'ha perdut la connexió amb la tele. Prem «Tria un dispositiu i emet» per reprendre.")
          return
        }
        setLastStop(`No s'ha pogut reprendre: ${err instanceof Error ? err.message : String(err)}`)
      })
    }
  }, [loadInto, discardSession])

  /** Opens the browser's own device picker. Must run from a user gesture. */
  const cast = useCallback(async (url: string, title: string) => {
    const context = contextRef.current
    const api = window.chrome?.cast
    // Saying nothing here is what made this button look broken.
    if (!context || !api) {
      setError('El SDK de Cast encara no ha carregat. Torna-ho a provar en un moment.')
      return
    }
    setError(null)
    setLastStop(null)
    stoppingRef.current = false

    try {
      if (!context.getCurrentSession()) await context.requestSession()
      const session = context.getCurrentSession()
      if (!session) {
        setError("No s'ha establert cap sessió amb el dispositiu.")
        syncRef.current()
        return
      }

      mediaRef.current = { url, title }
      attemptsRef.current = 0
      try {
        await loadInto(url, title)
      } catch (err) {
        if (!isDeadConnection(err)) throw err
        // Zombie session: drop it and open the picker for a fresh one. We are
        // still inside the user's tap, which the picker requires.
        discardSession()
        await context.requestSession()
        await loadInto(url, title)
      }
    } catch (err) {
      // The SDK rejects with a bare string code when the user simply cancels.
      const code = typeof err === 'string' ? err : (err as Error)?.message
      if (code !== 'cancel') {
        setError(
          `No s'ha pogut emetre: ${code ?? 'error desconegut'}` +
            (isDeadConnection(err) ? '. Si persisteix, recarrega la pàgina.' : ''),
        )
      }
    } finally {
      syncRef.current()
    }
  }, [loadInto, discardSession])

  const stop = useCallback(async () => {
    // Suppress auto-recovery: the receiver going idle is what we just asked for.
    stoppingRef.current = true
    mediaRef.current = null
    attemptsRef.current = 0
    const session = contextRef.current?.getCurrentSession()
    if (!session) {
      // Already gone; make sure the UI is not stuck showing "connected".
      syncRef.current()
      return
    }
    try {
      session.endSession(true)
    } catch (err) {
      if (isDeadConnection(err)) {
        // Nothing left to stop on the TV side; just forget the session.
        discardSession()
        return
      }
      setError(`No s'ha pogut aturar: ${err instanceof Error ? err.message : String(err)}`)
    }
    // Do not wait for an event that may never arrive: resync ourselves.
    setPlayerState(null)
    syncRef.current()
    window.setTimeout(() => syncRef.current(), 600)
  }, [discardSession])

  const setVolume = useCallback((level: number) => {
    setVolumeState(level)
    void contextRef.current?.getCurrentSession()?.setVolume(level)
  }, [])

  return { phase, deviceName, volume, error, playerState, lastStop, cast, stop, setVolume }
}
